import { ReactNode, useEffect, useRef } from 'react';
import { Z } from '@/lib/zIndex';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

interface ModalProps {
  open: boolean;
  title?: ReactNode;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  size?: ModalSize;
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const SIZE_TO_CLASS: Record<ModalSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

export function Modal({
  open,
  title,
  subtitle,
  onClose,
  children,
  footer,
  size = 'md',
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const prevOpenRef = useRef(false);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    if (open && !wasOpen) {
      restoreFocusRef.current = (document.activeElement as HTMLElement) ?? null;
      const root = dialogRef.current;
      if (root) {
        const items = root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
        (items[0] ?? root).focus();
      }
    }
    if (!open && wasOpen) {
      restoreFocusRef.current?.focus();
      restoreFocusRef.current = null;
    }
    prevOpenRef.current = open;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseRef.current?.();
        return;
      }
      if (e.key === 'Tab') {
        const root = dialogRef.current;
        if (!root) return;
        const items = Array.from(
          root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
        ).filter((el) => !el.hasAttribute('aria-hidden'));
        if (items.length === 0) {
          e.preventDefault();
          root.focus();
          return;
        }
        const first = items[0]!;
        const last = items[items.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const sizeCls = SIZE_TO_CLASS[size];

  return (
    <div className={`fixed inset-0 ${Z.MODAL} flex items-center justify-center bg-dash-bg/80 backdrop-blur-sm`}>
      <div
        className="absolute inset-0"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        className={`relative w-full ${sizeCls} mx-4 bg-dash-surface border border-dash-border rounded-lg shadow-xl focus:outline-none flex flex-col max-h-[90vh]`}
      >
        {title ? (
          <header className="px-5 py-4 border-b border-dash-border shrink-0">
            <h2 className="text-base font-semibold text-dash-primary">{title}</h2>
            {subtitle ? (
              <div className="text-xs text-dash-secondary mt-1">{subtitle}</div>
            ) : null}
          </header>
        ) : null}
        <div className="px-5 py-4 text-sm text-dash-primary overflow-y-auto">
          {children}
        </div>
        {footer ? (
          <footer className="px-5 py-3 border-t border-dash-border flex justify-end gap-2 shrink-0">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>
  );
}
